import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { invoiceService } from "../../services/invoice.service";
// import { customerService } from "../../services/customer.service";
import { showToast } from "../../lib/toast";
import { Printer, Download, Eye } from "lucide-react";
import { formatBusinessDate } from "../../lib/utils";

interface OutstandingCustomer {
  customerId: string;
  customerName: string;
  customerPhone: string;
  totalOutstanding: number;
  invoices: {
    id: string;
    invoiceNumber: string;
    invoiceDate: string;
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
    daysOverdue: number;
    agingBucket: string;
  }[];
}

export function OutstandingReportPage() {
  const [customers, setCustomers] = useState<OutstandingCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<"amount" | "days">("amount");
  const [selectedBucket, setSelectedBucket] = useState<string>("all");

  const agingBuckets = [
    { value: "all", label: "All Outstanding", days: null },
    { value: "0-30", label: "0-30 Days", days: 30 },
    { value: "31-60", label: "31-60 Days", days: 60 },
    { value: "61-90", label: "61-90 Days", days: 90 },
    { value: "90+", label: "90+ Days", days: null },
  ];

  useEffect(() => {
    loadOutstandingData();
  }, []);

  const loadOutstandingData = async () => {
    try {
      setLoading(true);
      // Get all pending/partial invoices
      const invoicesData = await invoiceService.getInvoices({
        paymentStatus: "PENDING,PARTIAL",
        limit: 1000,
      });

      // Group by customer
      const customerMap = new Map<string, OutstandingCustomer>();

      for (const invoice of invoicesData.data || []) {
        if (!invoice.customer) continue;

        const customerId = invoice.customer.id;
        const paidAmount =
          invoice.payments?.reduce(
            (sum: number, p: any) => sum + Number(p.amount),
            0,
          ) || 0;
        const pendingAmount = Number(invoice.totalAmount) - paidAmount;

        if (pendingAmount <= 0) continue;

        const invoiceDate = new Date(invoice.invoiceDate);
        const today = new Date();
        const daysOverdue = Math.floor(
          (today.getTime() - invoiceDate.getTime()) / (1000 * 60 * 60 * 24),
        );

        let agingBucket = "0-30";
        if (daysOverdue > 90) agingBucket = "90+";
        else if (daysOverdue > 60) agingBucket = "61-90";
        else if (daysOverdue > 30) agingBucket = "31-60";

        if (!customerMap.has(customerId)) {
          customerMap.set(customerId, {
            customerId,
            customerName: invoice.customer.name,
            customerPhone: invoice.customer.phone || "",
            totalOutstanding: 0,
            invoices: [],
          });
        }

        const customer = customerMap.get(customerId)!;
        customer.totalOutstanding += pendingAmount;
        customer.invoices.push({
          id: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          invoiceDate: invoice.invoiceDate,
          totalAmount: Number(invoice.totalAmount),
          paidAmount,
          pendingAmount,
          daysOverdue,
          agingBucket,
        });
      }

      setCustomers(Array.from(customerMap.values()));
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to load outstanding report",
      );
    } finally {
      setLoading(false);
    }
  };

  const getFilteredCustomers = () => {
    let filtered = [...customers];

    // Filter by aging bucket
    if (selectedBucket !== "all") {
      filtered = filtered
        .map((customer) => ({
          ...customer,
          invoices: customer.invoices.filter(
            (inv) => inv.agingBucket === selectedBucket,
          ),
        }))
        .filter((customer) => customer.invoices.length > 0);

      // Recalculate total outstanding
      filtered.forEach((customer) => {
        customer.totalOutstanding = customer.invoices.reduce(
          (sum, inv) => sum + inv.pendingAmount,
          0,
        );
      });
    }

    // Sort
    if (sortBy === "amount") {
      filtered.sort((a, b) => b.totalOutstanding - a.totalOutstanding);
    } else {
      filtered.sort((a, b) => {
        const aMaxDays = Math.max(...a.invoices.map((i) => i.daysOverdue));
        const bMaxDays = Math.max(...b.invoices.map((i) => i.daysOverdue));
        return bMaxDays - aMaxDays;
      });
    }

    return filtered;
  };

  const filteredCustomers = getFilteredCustomers();
  const totalOutstanding = filteredCustomers.reduce(
    (sum, c) => sum + c.totalOutstanding,
    0,
  );

  // Calculate aging summary
  const agingSummary = agingBuckets.slice(1).map((bucket) => {
    const total = customers.reduce((sum, customer) => {
      const bucketInvoices = customer.invoices.filter(
        (inv) => inv.agingBucket === bucket.value,
      );
      return sum + bucketInvoices.reduce((s, inv) => s + inv.pendingAmount, 0);
    }, 0);
    return { ...bucket, total };
  });

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    const headers = [
      "Customer Name",
      "Phone",
      "Invoice No.",
      "Invoice Date",
      "Total Amount",
      "Paid",
      "Pending",
      "Days Overdue",
      "Aging",
    ];

    const rows: any[] = [];
    filteredCustomers.forEach((customer) => {
      customer.invoices.forEach((invoice) => {
        rows.push([
          customer.customerName,
          customer.customerPhone,
          invoice.invoiceNumber,
          new Date(invoice.invoiceDate).toLocaleDateString("en-IN", {
            timeZone: "Asia/Kolkata",
          }),
          invoice.totalAmount.toFixed(2),
          invoice.paidAmount.toFixed(2),
          invoice.pendingAmount.toFixed(2),
          invoice.daysOverdue,
          invoice.agingBucket,
        ]);
      });
    });

    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `outstanding-report-${formatBusinessDate(new Date())}.csv`;
    a.click();

    showToast.success("Report exported successfully");
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 print:hidden">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Outstanding Report
            </h1>
            <p className="text-gray-600 mt-1">
              Customer-wise outstanding analysis
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-600">Total Outstanding</p>
          <p className="text-2xl font-bold text-red-600">
            ₹{totalOutstanding.toFixed(2)}
          </p>
        </div>
        {agingSummary.map((bucket) => (
          <div key={bucket.value} className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600">{bucket.label}</p>
            <p className="text-xl font-bold text-gray-900">
              ₹{bucket.total.toFixed(2)}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aging Bucket
            </label>
            <select
              value={selectedBucket}
              onChange={(e) => setSelectedBucket(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {agingBuckets.map((bucket) => (
                <option key={bucket.value} value={bucket.value}>
                  {bucket.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as "amount" | "days")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="amount">Outstanding Amount</option>
              <option value="days">Days Overdue</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customer List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
            Loading report...
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
            No outstanding invoices found
          </div>
        ) : (
          filteredCustomers.map((customer) => (
            <div
              key={customer.customerId}
              className="bg-white rounded-lg shadow-sm overflow-hidden"
            >
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {customer.customerName}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {customer.customerPhone}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Total Outstanding</p>
                    <p className="text-2xl font-bold text-red-600">
                      ₹{customer.totalOutstanding.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Invoice
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Total
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Paid
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Pending
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Days
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Aging
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase print:hidden">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {customer.invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(invoice.invoiceDate).toLocaleDateString(
                            "en-IN",
                            { timeZone: "Asia/Kolkata" },
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                          ₹{invoice.totalAmount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                          ₹{invoice.paidAmount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-red-600">
                          ₹{invoice.pendingAmount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                          {invoice.daysOverdue}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              invoice.agingBucket === "0-30"
                                ? "bg-blue-100 text-blue-800"
                                : invoice.agingBucket === "31-60"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : invoice.agingBucket === "61-90"
                                    ? "bg-orange-100 text-orange-800"
                                    : "bg-red-100 text-red-800"
                            }`}
                          >
                            {invoice.agingBucket}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center print:hidden">
                          <Link
                            to={`/invoices/${invoice.id}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Eye className="w-4 h-4 inline" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 1cm;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
